/**
 * Beta build indicator: a colour and an animal derived from the build ID, so anyone testing can see at
 * a glance which build a phone is running ("🐳 Blue Whale") and say so in a report. Shown as a stripe at
 * the top of the header, in the diagnostic report, and printed by `npm run deploy`.
 *
 * REMOVE AT PUBLIC LAUNCH (docs/deploy.md): this module, the header stripe in src/ui/app.ts, its
 * styles, the `build.animal.*` texts, the report's `build:` line and the name in tools/deployed-build.ts.
 *
 * No red or green (they read as findings: problem / fine), and no orange either (the "worth checking"
 * colour, --verify). Every pair of background and text passes WCAG AA (4.5:1; tested).
 */
export interface BuildAnimal {
  /** i18n key suffix: `build.animal.<id>`. */
  id: string;
  emoji: string;
  /** The English name (diagnostic report, deploy output); the UI uses the i18n text. */
  name: string;
  background: string;
  text: string;
}

export const BUILD_ANIMALS: readonly BuildAnimal[] = [
  { id: 'blueWhale', emoji: '🐳', name: 'Blue Whale', background: '#1d4ed8', text: '#ffffff' },
  { id: 'purpleOctopus', emoji: '🐙', name: 'Purple Octopus', background: '#7e22ce', text: '#ffffff' },
  { id: 'pinkFlamingo', emoji: '🦩', name: 'Pink Flamingo', background: '#be185d', text: '#ffffff' },
  { id: 'yellowBee', emoji: '🐝', name: 'Yellow Bee', background: '#facc15', text: '#1b1f24' },
  { id: 'blackPenguin', emoji: '🐧', name: 'Black Penguin', background: '#111827', text: '#ffffff' },
  { id: 'cyanDolphin', emoji: '🐬', name: 'Cyan Dolphin', background: '#0e7490', text: '#ffffff' },
  { id: 'skyButterfly', emoji: '🦋', name: 'Sky Butterfly', background: '#7dd3fc', text: '#0c1118' },
  { id: 'lilacSnail', emoji: '🐌', name: 'Lilac Snail', background: '#d8b4fe', text: '#1b1f24' },
  { id: 'indigoOwl', emoji: '🦉', name: 'Indigo Owl', background: '#312e81', text: '#ffffff' },
  { id: 'whiteSwan', emoji: '🦢', name: 'White Swan', background: '#ffffff', text: '#1b1f24' },
  { id: 'yellowChick', emoji: '🐥', name: 'Yellow Chick', background: '#facc15', text: '#1b1f24' },
  { id: 'yellowGiraffe', emoji: '🦒', name: 'Yellow Giraffe', background: '#facc15', text: '#1b1f24' },
  { id: 'yellowLadybug', emoji: '🐞', name: 'Yellow Ladybug', background: '#facc15', text: '#1b1f24' },
  { id: 'yellowCricket', emoji: '🦗', name: 'Yellow Cricket', background: '#facc15', text: '#1b1f24' },
  { id: 'yellowCaterpillar', emoji: '🐛', name: 'Yellow Caterpillar', background: '#facc15', text: '#1b1f24' },
  { id: 'lemonDuck', emoji: '🦆', name: 'Lemon Duck', background: '#fde047', text: '#1b1f24' },
  { id: 'butterHamster', emoji: '🐹', name: 'Butter Hamster', background: '#fef9c3', text: '#1b1f24' },
  { id: 'butterLlama', emoji: '🦙', name: 'Butter Llama', background: '#fef9c3', text: '#1b1f24' },
  { id: 'canaryParrot', emoji: '🦜', name: 'Canary Parrot', background: '#fef08a', text: '#1b1f24' },
  { id: 'canaryTurkey', emoji: '🦃', name: 'Canary Turkey', background: '#fef08a', text: '#1b1f24' },
  { id: 'turquoiseTurtle', emoji: '🐢', name: 'Turquoise Turtle', background: '#2dd4bf', text: '#1b1f24' },
  { id: 'turquoiseFrog', emoji: '🐸', name: 'Turquoise Frog', background: '#2dd4bf', text: '#1b1f24' },
  { id: 'tealCrocodile', emoji: '🐊', name: 'Teal Crocodile', background: '#0f766e', text: '#ffffff' },
  { id: 'tealLizard', emoji: '🦎', name: 'Teal Lizard', background: '#0f766e', text: '#ffffff' },
  { id: 'cyanSeal', emoji: '🦭', name: 'Cyan Seal', background: '#0e7490', text: '#ffffff' },
  { id: 'cyanFish', emoji: '🐠', name: 'Cyan Fish', background: '#0e7490', text: '#ffffff' },
  { id: 'aquaFish', emoji: '🐟', name: 'Aqua Fish', background: '#67e8f9', text: '#1b1f24' },
  { id: 'aquaShark', emoji: '🦈', name: 'Aqua Shark', background: '#67e8f9', text: '#1b1f24' },
  { id: 'aquaPufferfish', emoji: '🐡', name: 'Aqua Pufferfish', background: '#67e8f9', text: '#1b1f24' },
  { id: 'skyBird', emoji: '🐦', name: 'Sky Bird', background: '#7dd3fc', text: '#1b1f24' },
  { id: 'skyHatchling', emoji: '🐣', name: 'Sky Hatchling', background: '#7dd3fc', text: '#1b1f24' },
  { id: 'cobaltCrab', emoji: '🦀', name: 'Cobalt Crab', background: '#2563eb', text: '#ffffff' },
  { id: 'cobaltWhale', emoji: '🐋', name: 'Cobalt Whale', background: '#2563eb', text: '#ffffff' },
  { id: 'denimLobster', emoji: '🦞', name: 'Denim Lobster', background: '#1e5aa0', text: '#ffffff' },
  { id: 'sapphireEagle', emoji: '🦅', name: 'Sapphire Eagle', background: '#1e40af', text: '#ffffff' },
  { id: 'sapphireDinosaur', emoji: '🦖', name: 'Sapphire Dinosaur', background: '#1e40af', text: '#ffffff' },
  { id: 'navySquid', emoji: '🦑', name: 'Navy Squid', background: '#1e3a8a', text: '#ffffff' },
  { id: 'navyShrimp', emoji: '🦐', name: 'Navy Shrimp', background: '#1e3a8a', text: '#ffffff' },
  { id: 'indigoBat', emoji: '🦇', name: 'Indigo Bat', background: '#312e81', text: '#ffffff' },
  { id: 'indigoHorse', emoji: '🐴', name: 'Indigo Horse', background: '#312e81', text: '#ffffff' },
  { id: 'violetElephant', emoji: '🐘', name: 'Violet Elephant', background: '#6d28d9', text: '#ffffff' },
  { id: 'violetRabbit', emoji: '🐇', name: 'Violet Rabbit', background: '#6d28d9', text: '#ffffff' },
  { id: 'purpleHedgehog', emoji: '🦔', name: 'Purple Hedgehog', background: '#7e22ce', text: '#ffffff' },
  { id: 'purpleSnake', emoji: '🐍', name: 'Purple Snake', background: '#7e22ce', text: '#ffffff' },
  { id: 'lavenderSheep', emoji: '🐑', name: 'Lavender Sheep', background: '#c4b5fd', text: '#1b1f24' },
  { id: 'lavenderPoodle', emoji: '🐩', name: 'Lavender Poodle', background: '#c4b5fd', text: '#1b1f24' },
  { id: 'lilacUnicorn', emoji: '🦄', name: 'Lilac Unicorn', background: '#d8b4fe', text: '#1b1f24' },
  { id: 'lilacCat', emoji: '🐈', name: 'Lilac Cat', background: '#d8b4fe', text: '#1b1f24' },
  { id: 'plumBear', emoji: '🐻', name: 'Plum Bear', background: '#86198f', text: '#ffffff' },
  { id: 'plumBoar', emoji: '🐗', name: 'Plum Boar', background: '#86198f', text: '#ffffff' },
  { id: 'magentaFox', emoji: '🦊', name: 'Magenta Fox', background: '#a21caf', text: '#ffffff' },
  { id: 'magentaPeacock', emoji: '🦚', name: 'Magenta Peacock', background: '#a21caf', text: '#ffffff' },
  { id: 'fuchsiaPig', emoji: '🐷', name: 'Fuchsia Pig', background: '#c026d3', text: '#ffffff' },
  { id: 'fuchsiaChicken', emoji: '🐔', name: 'Fuchsia Chicken', background: '#c026d3', text: '#ffffff' },
  { id: 'roseCamel', emoji: '🐪', name: 'Rose Camel', background: '#db2777', text: '#ffffff' },
  { id: 'roseBunny', emoji: '🐰', name: 'Rose Bunny', background: '#db2777', text: '#ffffff' },
  { id: 'pinkKoala', emoji: '🐨', name: 'Pink Koala', background: '#be185d', text: '#ffffff' },
  { id: 'pinkMonkey', emoji: '🐵', name: 'Pink Monkey', background: '#be185d', text: '#ffffff' },
  { id: 'blushCat', emoji: '🐱', name: 'Blush Cat', background: '#fbcfe8', text: '#1b1f24' },
  { id: 'blushMouse', emoji: '🐭', name: 'Blush Mouse', background: '#fbcfe8', text: '#1b1f24' },
  { id: 'slateWolf', emoji: '🐺', name: 'Slate Wolf', background: '#475569', text: '#ffffff' },
  { id: 'slateBull', emoji: '🐂', name: 'Slate Bull', background: '#475569', text: '#ffffff' },
  { id: 'silverRat', emoji: '🐀', name: 'Silver Rat', background: '#cbd5e1', text: '#1b1f24' },
  { id: 'silverZebra', emoji: '🦓', name: 'Silver Zebra', background: '#cbd5e1', text: '#1b1f24' },
  { id: 'grayRhino', emoji: '🦏', name: 'Gray Rhino', background: '#6b7280', text: '#ffffff' },
  { id: 'grayHippo', emoji: '🦛', name: 'Gray Hippo', background: '#6b7280', text: '#ffffff' },
  { id: 'grayKangaroo', emoji: '🦘', name: 'Gray Kangaroo', background: '#6b7280', text: '#ffffff' },
  { id: 'charcoalGorilla', emoji: '🦍', name: 'Charcoal Gorilla', background: '#1f2937', text: '#ffffff' },
  { id: 'charcoalOrangutan', emoji: '🦧', name: 'Charcoal Orangutan', background: '#1f2937', text: '#ffffff' },
  { id: 'charcoalBison', emoji: '🦬', name: 'Charcoal Bison', background: '#1f2937', text: '#ffffff' },
  { id: 'charcoalBadger', emoji: '🦡', name: 'Charcoal Badger', background: '#1f2937', text: '#ffffff' },
  { id: 'blackLeopard', emoji: '🐆', name: 'Black Leopard', background: '#111827', text: '#ffffff' },
  { id: 'blackTiger', emoji: '🐅', name: 'Black Tiger', background: '#111827', text: '#ffffff' },
  { id: 'blackLion', emoji: '🦁', name: 'Black Lion', background: '#111827', text: '#ffffff' },
  { id: 'blackCow', emoji: '🐮', name: 'Black Cow', background: '#111827', text: '#ffffff' },
  { id: 'blackDog', emoji: '🐶', name: 'Black Dog', background: '#111827', text: '#ffffff' },
  { id: 'blackAnt', emoji: '🐜', name: 'Black Ant', background: '#111827', text: '#ffffff' },
  { id: 'blackScorpion', emoji: '🦂', name: 'Black Scorpion', background: '#111827', text: '#ffffff' },
  { id: 'blackRaccoon', emoji: '🦝', name: 'Black Raccoon', background: '#111827', text: '#ffffff' },
  { id: 'blackSkunk', emoji: '🦨', name: 'Black Skunk', background: '#111827', text: '#ffffff' },
  { id: 'whiteGoat', emoji: '🐐', name: 'White Goat', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteDeer', emoji: '🦌', name: 'White Deer', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteBeaver', emoji: '🦫', name: 'White Beaver', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteOtter', emoji: '🦦', name: 'White Otter', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteSloth', emoji: '🦥', name: 'White Sloth', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteDodo', emoji: '🦤', name: 'White Dodo', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteBuffalo', emoji: '🐃', name: 'White Buffalo', background: '#ffffff', text: '#1b1f24' },
  { id: 'whiteRam', emoji: '🐏', name: 'White Ram', background: '#ffffff', text: '#1b1f24' },
  { id: 'snowPanda', emoji: '🐼', name: 'Snow Panda', background: '#f8fafc', text: '#1b1f24' },
  { id: 'snowRooster', emoji: '🐓', name: 'Snow Rooster', background: '#f8fafc', text: '#1b1f24' },
  { id: 'snowCow', emoji: '🐄', name: 'Snow Cow', background: '#f8fafc', text: '#1b1f24' },
  { id: 'snowRacehorse', emoji: '🐎', name: 'Snow Racehorse', background: '#f8fafc', text: '#1b1f24' },
  { id: 'snowChick', emoji: '🐤', name: 'Snow Chick', background: '#f8fafc', text: '#1b1f24' },
  { id: 'indigoMammoth', emoji: '🦣', name: 'Indigo Mammoth', background: '#312e81', text: '#ffffff' },
  { id: 'grayDog', emoji: '🐕', name: 'Gray Dog', background: '#6b7280', text: '#ffffff' },
  { id: 'grayGuideDog', emoji: '🦮', name: 'Gray Guide Dog', background: '#6b7280', text: '#ffffff' },
  { id: 'whitePig', emoji: '🐖', name: 'White Pig', background: '#ffffff', text: '#1b1f24' },
  { id: 'silverMouse', emoji: '🐁', name: 'Silver Mouse', background: '#cbd5e1', text: '#1b1f24' },
  { id: 'turquoiseDragon', emoji: '🐉', name: 'Turquoise Dragon', background: '#2dd4bf', text: '#1b1f24' },
  { id: 'lilacDragon', emoji: '🐲', name: 'Lilac Dragon', background: '#d8b4fe', text: '#1b1f24' },
  { id: 'skyTiger', emoji: '🐯', name: 'Sky Tiger', background: '#7dd3fc', text: '#1b1f24' },
  { id: 'tealMonkey', emoji: '🐒', name: 'Teal Monkey', background: '#0f766e', text: '#ffffff' },
];

/** Minutes since 1970 of the build ID's stamp ("+20261010-2105-"), undefined for an ID without one. */
function stampMinutes(buildId: string): number | undefined {
  const m = /\+(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})-/.exec(buildId);
  if (!m) return undefined;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  return Math.floor(Date.UTC(y, mo - 1, d, h, mi) / 60000);
}

/** One fixed shuffle of 0..n-1 (mulberry32 seeded with a constant, Fisher-Yates): the order the animals come in. */
function animalOrder(n: number): number[] {
  let s = 0x7f4a7c15;
  const next = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [order[i], order[j]] = [order[j] as number, order[i] as number];
  }
  return order;
}

/**
 * The same build ID always gives the same animal. The animals come in one fixed shuffled order, one step per build
 * minute, so two builds less than one full round (the number of animals, in minutes) apart never share an animal
 * (any window of that many consecutive minutes uses each animal once). An ID without a stamp falls back to FNV-1a
 * over the whole ID. Builds further apart can repeat: no state is kept between builds.
 */
export function buildAnimal(buildId: string): BuildAnimal {
  const n = BUILD_ANIMALS.length;
  const minutes = stampMinutes(buildId);
  if (minutes === undefined) {
    let h = 0x811c9dc5;
    for (const ch of buildId) h = Math.imul(h ^ (ch.codePointAt(0) ?? 0), 0x01000193) >>> 0;
    return BUILD_ANIMALS[h % n] as BuildAnimal;
  }
  const order = animalOrder(n);
  const pos = minutes % n;
  return BUILD_ANIMALS[order[pos] as number] as BuildAnimal;
}

/**
 * The commit hash in the build ID ("c2b851d", "-dirty" kept): animals repeat across builds, so the hash is what
 * identifies one (Matt, 2026-10-19). Empty for an ID without one.
 */
export const buildHash = (buildId: string): string => /-([0-9a-f]{7,40}(?:-dirty)?)$/.exec(buildId)?.[1] ?? '';

/**
 * "🐳 Blue Whale · c2b851d" (English): the animal at a glance, the hash to identify the build. `hash: false` where the
 * full build ID is printed right beside it (the report's version line, the deploy line).
 */
export const buildAnimalLabel = (buildId: string, opts: { hash?: boolean } = {}): string => {
  const a = buildAnimal(buildId);
  const hash = opts.hash === false ? '' : buildHash(buildId);
  return `${a.emoji} ${a.name}${hash ? ` · ${hash}` : ''}`;
};
